import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { EVENT_SALES_LABEL, EVENT_SALES_COLOR } from '../lib/constants.js';
import { eventDateRange } from '../lib/events.js';
import { todayStr } from '../lib/format.js';

// ─── Events calendar settings (Settings → Events) ─────────────────────────
// Manager + admin CRUD for operations events (parades, races, town
// closures). Migration 048 + /api/admin/events. CJ feedback 2026-05-09.
export function EventsSettingsView({ currentUser, locations }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | <id>
  const [draft, setDraft] = useState(null);
  const [savingMsg, setSavingMsg] = useState('');

  const blankDraft = () => ({
    title: '', start_date: todayStr(), end_date: '',
    location_ids: [], staffing_restricted: false, sales_impact: 'unknown',
    is_annual: false, url: '', notes: '',
  });

  async function load() {
    setLoading(true);
    try {
      const r = await api('GET', '/api/admin/events', null, headers);
      setEvents(Array.isArray(r?.events) ? r.events : []);
    } catch (_) { setEvents([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEditing('new'); setDraft(blankDraft()); }
  function openEdit(e) {
    setEditing(e.id);
    setDraft({
      title: e.title || '',
      start_date: (e.start_date || '').slice(0, 10),
      end_date: (e.end_date || '').slice(0, 10) || '',
      location_ids: Array.isArray(e.location_ids) ? e.location_ids.map(Number) : [],
      staffing_restricted: !!e.staffing_restricted,
      sales_impact: e.sales_impact || 'unknown',
      is_annual: !!e.is_annual,
      url: e.url || '',
      notes: e.notes || '',
    });
  }
  function close() { setEditing(null); setDraft(null); setSavingMsg(''); }

  async function save() {
    if (!draft) return;
    if (!draft.title.trim()) { setSavingMsg('Title required.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.start_date)) { setSavingMsg('Pick a start date.'); return; }
    if (draft.end_date && draft.end_date < draft.start_date) {
      setSavingMsg('End date must be on or after start date.'); return;
    }
    setSavingMsg('Saving…');
    try {
      const payload = {
        title: draft.title.trim(),
        start_date: draft.start_date,
        end_date: draft.end_date || null,
        location_ids: draft.location_ids,
        staffing_restricted: !!draft.staffing_restricted,
        sales_impact: draft.sales_impact,
        is_annual: !!draft.is_annual,
        url: draft.url || null,
        notes: draft.notes || null,
      };
      if (editing === 'new') await api('POST', '/api/admin/events', payload, headers);
      else                   await api('PUT', `/api/admin/events/${editing}`, payload, headers);
      close(); await load();
    } catch (e) { setSavingMsg(`Failed: ${e.message || e}`); }
  }
  async function remove(e) {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    try { await api('DELETE', `/api/admin/events/${e.id}`, null, headers); await load(); }
    catch (err) { alert(err.message || String(err)); }
  }

  function locLabel(ids) {
    if (!ids || ids.length === 0) return 'All locations';
    return ids.map((id) => locations.find((l) => l.id === id)?.name || `#${id}`).join(', ');
  }

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                      flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div>
          <div className="text-base font-semibold mb-1">Events calendar</div>
          <div className="text-sm text-gray-500">
            Track local events that affect operations: parades, marathons, festivals, town closures.
            Surfaces on the Daily tab and Build Schedule.
          </div>
        </div>
        <button onClick={openNew}
          style={{ padding: '8px 14px', background: 'var(--gold)', color: '#fff',
                    border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          + New event
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : events.length === 0 ? (
        <div className="text-sm text-gray-500" style={{ fontStyle: 'italic' }}>
          No events yet. Add the first one with the button above.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {events.map((e) => {
            const c = EVENT_SALES_COLOR[e.sales_impact] || EVENT_SALES_COLOR.unknown;
            return (
              <div key={e.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                                gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{e.title}</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      {eventDateRange(e)} · {locLabel(e.location_ids)}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                      background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
                        {EVENT_SALES_LABEL[e.sales_impact] || e.sales_impact}
                      </span>
                      {e.staffing_restricted && (
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                        background: '#fef9ee', color: '#8e6c33', border: '1px solid #f5e6c8' }}>
                          🔒 Restricted staffing
                        </span>
                      )}
                      {e.is_annual && (
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                        background: '#e8f5fe', color: '#1565c0', border: '1px solid #b3d6f5' }}>
                          🔁 Annual
                        </span>
                      )}
                      {e.url && (
                        <a href={e.url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                    background: '#fff', color: '#1976d2', border: '1px solid #d0d4d9',
                                    textDecoration: 'none' }}>
                          🔗 Website
                        </a>
                      )}
                    </div>
                    {e.notes && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#444' }}>{e.notes}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => openEdit(e)}
                      style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d0d4d9',
                                borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => remove(e)}
                      style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #fecaca',
                                borderRadius: 6, background: '#fef2f2', color: '#a13030', cursor: 'pointer' }}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && draft && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,31,29,0.45)', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={close}>
          <div onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 12, padding: 18, maxWidth: 560, width: '100%',
                          maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
              {editing === 'new' ? 'New event' : 'Edit event'}
            </div>

            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Title *</label>
            <input type="text" value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="e.g. 4th of July Parade"
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8,
                        fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }} />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
                Start date *
                <input type="date" value={draft.start_date}
                  onChange={(e) => {
                    const next = e.target.value;
                    // If the new start is past the current end, clear end so
                    // the user reopens the calendar from a sensible position.
                    setDraft((d) => ({
                      ...d,
                      start_date: next,
                      end_date: d.end_date && next && d.end_date < next ? '' : d.end_date,
                    }));
                  }}
                  style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
                End date <span style={{ color: '#888' }}>(blank = single day)</span>
                <input type="date" value={draft.end_date}
                  min={draft.start_date || undefined}
                  onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
                  style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13 }} />
              </label>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                Locations affected <span style={{ color: '#888' }}>(none checked = all locations)</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {locations.map((l) => {
                  const checked = draft.location_ids.includes(l.id);
                  return (
                    <label key={l.id} style={{ fontSize: 12, padding: '4px 12px',
                                                  border: '1px solid #d0d4d9', borderRadius: 99,
                                                  cursor: 'pointer',
                                                  background: checked ? 'var(--gold)' : '#fff',
                                                  color: checked ? '#fff' : '#333' }}>
                      <input type="checkbox" checked={checked} style={{ display: 'none' }}
                        onChange={() => setDraft({
                          ...draft,
                          location_ids: checked
                            ? draft.location_ids.filter((id) => id !== l.id)
                            : [...draft.location_ids, l.id],
                        })} />
                      {l.name}
                    </label>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
                Sales impact
                <select value={draft.sales_impact}
                  onChange={(e) => setDraft({ ...draft, sales_impact: e.target.value })}
                  style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13 }}>
                  <option value="unknown">Unknown</option>
                  <option value="increase">↑ Increases sales</option>
                  <option value="decrease">↓ Decreases sales</option>
                  <option value="neutral">Neutral</option>
                </select>
              </label>
              <label style={{ fontSize: 12, color: '#555', alignSelf: 'flex-end',
                                display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={draft.staffing_restricted}
                  onChange={(e) => setDraft({ ...draft, staffing_restricted: e.target.checked })} />
                Restricted staffing date
              </label>
            </div>

            <label style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center',
                              gap: 8, marginBottom: 10, padding: '8px 12px',
                              background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 6 }}>
              <input type="checkbox" checked={draft.is_annual}
                onChange={(e) => setDraft({ ...draft, is_annual: e.target.checked })} />
              <span><strong>🔁 Recurs annually</strong> — same month + day every year. Edit this row if a future year's date moves; delete it when the event stops.</span>
            </label>

            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Website URL</label>
            <input type="url" value={draft.url}
              onChange={(e) => setDraft({ ...draft, url: e.target.value })}
              placeholder="https://example.com/event"
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8,
                        fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }} />

            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Notes</label>
            <textarea value={draft.notes} rows={3}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="e.g. Store closes at 1pm. Expect heavy parade traffic until 3pm."
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8,
                        fontSize: 13, fontFamily: 'inherit', marginBottom: 10, boxSizing: 'border-box' }} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
              {savingMsg && (
                <span style={{ fontSize: 12, color: savingMsg.startsWith('Failed') || savingMsg.includes('required') ? '#b00020' : '#666' }}>
                  {savingMsg}
                </span>
              )}
              <button onClick={close}
                style={{ fontSize: 13, padding: '6px 14px', border: '1px solid #d0d4d9',
                          background: '#fff', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save}
                style={{ fontSize: 13, padding: '6px 16px', border: 0, background: 'var(--gold)',
                          color: '#fff', fontWeight: 600, borderRadius: 6, cursor: 'pointer' }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
